import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const MagicLinkVerify = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("Please wait while we sign you in securely.");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [redirectTo, setRedirectTo] = useState("/browse-candidates");
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    const redirectParam = searchParams.get('redirectTo') || '/browse-candidates';
    setRedirectTo(redirectParam);

    if (token) {
      verifyMagicLink(token, redirectParam);
    } else {
      setStatus('error');
      setMessage('No verification token found. Please try logging in again.');
    }
  }, [searchParams, navigate]);

  const verifyMagicLink = async (token, redirectTo) => {
    try {
      const response = await axios.post("/api/auth/verify-magic-link", { token });

      setStatus("success");
      setMessage("You've been signed in successfully. Redirecting...");
      setUser(response.data.user);

      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
      }

      // Check onboarding status and redirect accordingly
      let finalRedirectTo = redirectTo;
      if (response.data.onboarding && !response.data.onboarding.isComplete) {
        // If onboarding is incomplete, redirect to onboarding form
        const companyId = response.data.user.companyId;
        finalRedirectTo = companyId ? `/new-onboarding?companyId=${companyId}` : '/new-onboarding';
        setMessage("Setting up your profile. Redirecting to onboarding...");
        console.log(`Onboarding incomplete for company ${response.data.onboarding.companyName}. Redirecting to onboarding.`);
      }

      // Redirect after successful login
      setTimeout(() => {
        navigate(finalRedirectTo, { replace: true });
      }, 2000);

    } catch (err) {
      setStatus("error");
      if (err.response && err.response.data) {
        setMessage(err.response.data.message);
        if (err.response.data.email) {
          setEmail(err.response.data.email);
        }
      } else {
        setMessage("An error occurred while verifying your magic link.");
      }
    }
  };

  // Request a fresh magic link for the stored email
  const requestNewLink = async () => {
    if (!email) {
      // Fallback – navigate to login page if we don’t know their email
      navigate("/passwordless-login");
      return;
    }

    setResending(true);
    setMessage("");
    try {
      await axios.post("/api/auth/request-magic-link", {
        email,
        redirectTo,
      });
      setSent(true);
      setMessage(`We've sent a new sign-in link to ${email}. Please check your inbox.`);
    } catch (e) {
      if (e.response && e.response.data) {
        setMessage(e.response.data.message);
      } else {
        setMessage("Failed to send a new magic link. Please try again later.");
      }
    } finally {
      setResending(false);
    }
  };

  const renderIcon = () => {
    switch (status) {
      case "verifying":
        return (
          <div className="mx-auto w-16 h-16 border-4 border-gray-200 border-t-[#0fb592] rounded-full animate-spin"></div>
        );
      case "success":
        return (
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (status) {
      case "verifying":
        return "Verifying your link...";
      case "success":
        return `Welcome back, ${user?.firstName || 'friend'}!`;
      case "error":
        return "Verification Failed";
      default:
        return "";
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-6">
      <div className="absolute top-6 left-6">
        <img
          src="https://www.jumpstart-uk.com/wp-content/uploads/2022/05/jumpstart-logo-black.svg"
          className="h-8"
          alt="Jumpstart Logo"
        />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-6">
          {renderIcon()}
        </div>
        <h1 className="text-3xl font-medium text-gray-900 mb-3">
          {getTitle()}
        </h1>
        <p className="text-gray-600 text-lg mb-8">
          {message}
        </p>

        {status === 'error' && !sent && (
          <button
            onClick={requestNewLink}
            disabled={resending}
            className="bg-[#0fb592] text-white py-3 px-6 text-base font-medium rounded-xl hover:bg-[#0da578] focus:outline-none focus:ring-2 focus:ring-[#0fb592] focus:ring-offset-2 disabled:opacity-50 transition-colors duration-200"
          >
            {resending ? 'Sending…' : 'Send me a new link'}
          </button>
        )}

        {status === 'error' && sent && (
          <button
            onClick={() => navigate("/passwordless-login")}
            className="mt-4 text-[#0fb592] underline"
          >
            Didn’t get it? Try another email
          </button>
        )}
      </div>
    </div>
  );
};

export default MagicLinkVerify; 